'use client';

import { forwardRef, useMemo, useState } from 'react';
import { HslStringColorPicker } from 'react-colorful';
import { cn } from '@r/utils/utils.ts';
import { useForwardedRef } from '@r/hooks/use-forwarded-ref.ts';
import type { ButtonProps } from '@ui/button.tsx';
import { Button } from '@ui/button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/popover.tsx';
import { Input } from '@ui/input.tsx';
import { colorValidator } from '@r/utils/utils.ts';
import { z } from 'zod';
const validColorSchema = z.string().refine(colorValidator);

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

const ColorPicker = forwardRef<
  HTMLInputElement,
  Omit<ButtonProps, 'value' | 'onChange' | 'onBlur'> & ColorPickerProps
>(({ disabled, value, onChange, name, className, ...props }, forwardedRef) => {
  const ref = useForwardedRef(forwardedRef);
  const [open, setOpen] = useState(false);

  const parsedValue = useMemo(() => {
    return value || 'hsl(360, 0%, 0%)';
  }, [value]);

  const onOpenChangeHandler = (isOpen: boolean) => {
    if (!isOpen) {
      try {
        validColorSchema.parse(parsedValue);
      } catch {
        onChange('hsl(360, 0%, 0%)');
      }
    }
    setOpen(isOpen);
  };

  return (
    <Popover onOpenChange={onOpenChangeHandler} open={open}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          {...props}
          className={cn('block', className)}
          name={name}
          onClick={() => {
            setOpen(true);
          }}
          size="icon"
          style={{
            backgroundColor: parsedValue
          }}
          variant="outline"
        >
          <div />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full">
        <HslStringColorPicker color={parsedValue} onChange={onChange} />
        <Input
          maxLength={19}
          onChange={(e) => {
            onChange(e?.currentTarget?.value);
          }}
          ref={ref}
          value={parsedValue}
        />
      </PopoverContent>
    </Popover>
  );
});
ColorPicker.displayName = 'ColorPicker';

export { ColorPicker };
